import { FadeUp, SectionLabel } from "@/components/Reveal";
import { GraduationCap, Users, MapPin, PenLine, FileStack, ClipboardList } from "lucide-react";

const TEAM = [
  { role: "Secretary", duty: "Pleadings & contracts preparation", icon: PenLine },
  { role: "Legal Clerk", duty: "Court filings & registry liaison", icon: FileStack },
  { role: "Legal Clerk", duty: "Client records & general clerical work", icon: ClipboardList },
];

const TIMELINE = [
  {
    year: "2012 — 2016",
    title: "Legal Internship",
    body: "Began his legal career as an intern at Joseph Munyithya & Company Advocates while completing his studies.",
  },
  {
    year: "2014",
    title: "LL.B (Hons), University of Nairobi",
    body: "Graduated with a Bachelor of Laws, later earning a Postgraduate Diploma from the Kenya School of Law.",
  },
  {
    year: "31.01.2017",
    title: "Admitted to the Bar",
    body: "Admitted as an Advocate of the High Court of Kenya; retained as an associate at Munyithya, Mutugi, Umara & Muzna Co. Advocates.",
  },
  {
    year: "04.2018",
    title: "Firm Incorporated",
    body: "Founded the firm as sole practitioner — Sakim Company Advocates, now Musesya & Company Advocates.",
  },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <div>
            <FadeUp>
              <SectionLabel>The Advocate</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
                Samson Kivati <span className="text-gold">Musesya</span>
              </h2>
              <p className="font-editorial italic text-xl text-ink-dim mt-3">
                Sole Proprietor &amp; Advocate of the High Court of Kenya
              </p>
            </FadeUp>

            <FadeUp delay={0.15}>
              <p className="mt-8 text-ink-dim leading-relaxed">
                Mr. Musesya holds a Master of Tax and Customs Administration
                (Moi University, administered by KESRA) and brings wide
                experience in tax and commercial litigation, arbitration,
                consultancy, conveyancing, procurement and debt recovery —
                handling complex matters for a diverse corporate clientele.
              </p>
              <p className="mt-4 text-ink-dim leading-relaxed">
                From drafting opinions and pleadings to courtroom
                representation, his practice pairs rigorous legal research with
                prompt, accountable client reporting.
              </p>
            </FadeUp>

            <FadeUp delay={0.25}>
              <div className="mt-10 space-y-4">
                <div className="flex items-start gap-4 border border-gold/15 rounded-2xl p-5 bg-navy-900/40">
                  <GraduationCap className="text-gold shrink-0 mt-0.5" size={20} />
                  <p className="text-sm text-ink-dim">
                    LL.B (Hons) UoN · Dip. K.S.L. · Master of Tax &amp; Customs
                    Administration, Moi University (KESRA)
                  </p>
                </div>
                <div className="flex items-start gap-4 border border-gold/15 rounded-2xl p-5 bg-navy-900/40">
                  <Users className="text-gold shrink-0 mt-0.5" size={20} />
                  <p className="text-sm text-ink-dim">
                    Supported by an experienced secretary and two clerks —
                    pleadings, contracts, court filings and general clerical work.
                  </p>
                </div>
                <div className="flex items-start gap-4 border border-gold/15 rounded-2xl p-5 bg-navy-900/40">
                  <MapPin className="text-gold shrink-0 mt-0.5" size={20} />
                  <p className="text-sm text-ink-dim">
                    Muthaiga Square, 2nd Floor Room 15, Thika Road, Nairobi.
                  </p>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.35}>
              <div className="mt-8" data-testid="support-team-row">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-4">
                  Support Team
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {TEAM.map((m) => (
                    <div
                      key={m.role}
                      className="rounded-2xl border border-gold/15 bg-navy-700/50 p-4 text-center"
                    >
                      <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold">
                        <m.icon size={16} />
                      </span>
                      <div className="text-sm font-medium">{m.role}</div>
                      <div className="text-xs text-ink-muted mt-1">{m.duty}</div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>

          <div className="relative">
            <FadeUp delay={0.1} className="relative">
              <div className="absolute -inset-4 border border-gold/20 rounded-3xl -translate-x-4 -translate-y-4 -z-10" />
              <div
                className="relative rounded-3xl overflow-hidden border border-gold/25 shadow-[0_0_60px_rgba(154,136,15,0.10)]"
                data-testid="advocate-portrait-frame"
              >
                <img
                  src="/assets/portrait.jpg"
                  alt="Samson Kivati Musesya, Advocate of the High Court of Kenya"
                  className="w-full h-[30rem] lg:h-[34rem] object-cover object-[50%_22%]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/10 to-transparent" />
                <img
                  src="/assets/logo.jpg"
                  alt=""
                  aria-hidden="true"
                  className="absolute top-5 right-5 h-14 w-14 rounded-full object-cover ring-2 ring-gold/60"
                />
                <div className="absolute bottom-0 inset-x-0 p-7">
                  <div className="font-display text-2xl">Samson Kivati Musesya</div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold mt-2">
                    Sole Proprietor &middot; Advocate, High Court of Kenya
                  </div>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.2} className="relative mt-6">
              <div className="absolute -inset-4 border border-gold/20 rounded-3xl translate-x-4 translate-y-4 -z-10" />
              <img
                src="https://images.pexels.com/photos/29069329/pexels-photo-29069329.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Nairobi skyline at twilight"
                className="rounded-3xl w-full h-64 object-cover"
                data-testid="about-nairobi-image"
              />
            </FadeUp>

            <div className="mt-12 space-y-0">
              {TIMELINE.map((t, i) => (
                <FadeUp key={t.year} delay={i * 0.1}>
                  <div className="flex gap-6 group" data-testid={`timeline-item-${i}`}>
                    <div className="flex flex-col items-center">
                      <span className="h-3 w-3 rounded-full border-2 border-gold bg-navy-950 group-hover:bg-gold transition-colors duration-300 shrink-0" />
                      {i < TIMELINE.length - 1 && (
                        <span className="w-px flex-1 bg-gold/20" />
                      )}
                    </div>
                    <div className="pb-10">
                      <div className="font-mono text-xs text-gold tracking-[0.2em]">{t.year}</div>
                      <h3 className="font-display text-lg mt-1.5">{t.title}</h3>
                      <p className="text-sm text-ink-dim mt-2 leading-relaxed">{t.body}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
